/**
 * Inline reactions pubsub handlers, mirroring the inline-comments pubsub flow
 * (`@confluence/comments-pubsub/src/inline/rendererInlineCommentsPubSub.ts`).
 *
 * Three new AGG fields ride on the existing `confluence_onContentModified`
 * subscription:
 *   - `inlineReactionAdded`              → User added a reaction (creates a
 *     new highlight when `newHighlight=true`, otherwise increments an
 *     existing reaction count).
 *   - `inlineReactionRemoved`            → A reaction was removed but the
 *     highlight still has other reactions remaining.
 *   - `inlineReactionHighlightDeleted`   → The last reaction was removed,
 *     causing the anchor comment to be deleted and the ADF annotation mark
 *     to be removed.
 *
 */

import type { RefObject } from 'react';

import { getLogger } from '@confluence/logger';

import { AnnotationTypes } from '@atlaskit/adf-schema';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type RendererActions from '@atlaskit/renderer/actions';

import type {
	ConfluenceOnContentModified,
	SubscriptionsDispatchType,
} from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import type {
	AddCommentInCacheProps,
	UseAllCommentsDataReturn,
	getCommentsDataFromCache,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import type { getProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';

import { extractNumericCommentId } from '../utils/extractNumericCommentId.ts';
import { isInlineReactionThread } from '../utils/isInlineReactionThread.ts';
import { syncInlineReactionCommentToPanel } from '../utils/syncInlineReactionCommentToPanel.ts';

const logger = getLogger('experiment-inline-reactions');

type ProcessCommentsData = ReturnType<
	typeof getProcessedCommentsDataActions
>['processCommentsData'];

export type InlineReactionsContentType = 'page' | 'blogpost';

export type ShowReactionFlag = (event: {
	commentId: string;
	markerRef: string;
	emojiId?: string;
	contentType: InlineReactionsContentType;
	/**
	 * AAID of the user that produced the reaction. Receivers can use this to
	 * resolve the actor's display name via `confluence_reactedUsers` for the
	 * "{name} reacted to this page" flag copy.
	 */
	actorAccountId?: string;
}) => void;

export type RefetchReactionsForComment = (commentId: string) => void;

export type SyncLegacyCommentsDataReactionsSummary = (params: {
	commentId: string;
	markerRef: string;
	emojiId: string;
	actionType: 'add' | 'delete';
}) => void;

export type SyncLegacyCommentsDataReactionThread = (params: {
	commentId: string;
	markerRef: string;
}) => void | Promise<void>;

export type InlineReactionsPubSubParams = {
	contentId: string;
	contentType?: InlineReactionsContentType;
	currentUserId: string | null;
	rendererActions?: RendererActions;
	updateDocument?: (newAdf: JSONDocNode) => void;
	/**
	 * A ref to the receiver's currently loaded document version number.
	 * Passed as a ref (not a scalar) so the handler closure always reads
	 * the live value at event time — without causing the subscription to be
	 * torn down and re-established on every version bump.
	 */
	publishedDocumentVersionRef?: RefObject<number | undefined>;
	/**
	 * View mode can safely apply renderer annotation changes when event step
	 * data matches the loaded document. Editor/live docs should stay
	 * conservative and rely on refresh/refetch flows instead.
	 */
	allowDocumentMutation?: boolean;
	pageMode?: PageMode;
	addOptimisticReaction: (markerRef: string, commentId: string) => void;
	/**
	 * Clears a locally-resolved marker when the server confirms the inline
	 * reaction was reopened by another user.
	 */
	removeOptimisticResolvedMarkerRef?: (markerRef: string) => void;
	/**
	 * Returns true when this client has locally resolved the marker and is waiting
	 * for server data to catch up. Reopen handling uses this to avoid fetching
	 * every regular inline comment reopen event.
	 */
	isOptimisticallyResolvedMarkerRef?: (markerRef: string) => boolean;
	/**
	 * Drops local optimistic markers for the given markerRef, used when the
	 * highlight is deleted by another user.
	 */
	pruneOptimisticReactions: (confirmedMarkerRefs: readonly string[]) => void;
	/**
	 * Triggers a refetch of `confluence_contentReactionsSummary` for the
	 * affected commentId so the receiver's `count` and `reacted` fields are
	 * authoritative (the pubsub `reacted` field reflects the originator's
	 * state, not the receiver's).
	 */
	refetchReactionsSummary: RefetchReactionsForComment;
	/**
	 * Triggers a refetch of `confluence_activeInlineData` so the new
	 * markerRef → commentId mapping is propagated to viewers.
	 */
	refetchActiveInlineData: () => void;
	/**
	 * Marks the receiver's loaded document as out-of-date, triggering a full
	 * page-content refetch that delivers the new annotation mark via the
	 * published ADF. Called when the pubsub event does not include position
	 * data (`step`/`publishVersionNumber`) or the receiver's doc version
	 * does not match the event's version (i.e. the mark cannot be applied
	 * directly without risking a position mismatch).
	 */
	markDocumentNeedsUpdate: () => void;
	/**
	 * Pushes a "{somebody} reacted to this page" flag through the FlagsStateContainer.
	 */
	showReactionFlag: ShowReactionFlag;
	/**
	 * Applies a remote reaction delta (+1 or -1) directly to the store for an
	 * instant UI update before the background refetch completes.
	 *
	 * Called with the numeric commentId (not ARI), the emojiId, and the delta
	 * direction. Only invoked when the event payload includes `emojiId`.
	 */
	applyRemoteDelta: (commentId: string, emojiId: string, delta: 1 | -1) => void;
	/**
	 * Records that the reactor list for a comment+emoji pair changed, so the next
	 * mounted facepile fetcher refetches the reacted users from the network. This
	 * is the always-mounted source that covers the case where the popup/tile is
	 * closed when the remote event arrives. Called with the numeric commentId.
	 */
	markReactedUsersChanged: (commentId: string, emojiId: string) => void;
	/**
	 * Updates the legacy comments-data store for an already-open sidebar. This
	 * preserves the viewer's `reacted` state because pubsub events are authored
	 * by another user.
	 */
	syncLegacyCommentsDataReactionsSummary?: SyncLegacyCommentsDataReactionsSummary;
	/**
	 * Adds a brand new inline reaction thread to the legacy comments-data store
	 * for an already-open sidebar. Summary sync cannot handle this case because
	 * the markerRef does not exist in the legacy map yet.
	 */
	syncLegacyCommentsDataReactionThread?: SyncLegacyCommentsDataReactionThread;
	/**
	 * Optional: clears the active highlight if it matches the markerRef of a
	 * deleted highlight, so the renderer doesn't keep stale focus state.
	 */
	setActiveHighlight?: (markerRef: string | null) => void;
	/**
	 * Optional: writes a fetched comment into the comments panel's Apollo/Relay
	 * cache so newly added reactions appear in the panel without a page refresh.
	 * Matches the pattern used by the inline comments pubsub handler.
	 */
	addCommentInCache?: ({ newComment, onError }: AddCommentInCacheProps) => void;
	/**
	 * Optional: removes a deleted reaction thread from the comments panel cache.
	 */
	deleteCommentInCache?: UseAllCommentsDataReturn['deleteCommentInCache'];
	/**
	 * Optional: fetches a comment by ID and writes it into the shared cache.
	 * Used together with addCommentInCache to update the comments panel.
	 */
	fetchCommentById?: typeof fetchCommentById;
	/**
	 * Optional: marks the reaction comment as unread so the content wrapper
	 * shows the blue-dot indicator — matching inline comment pubsub behaviour.
	 */
	updateUnreadCommentIDSet?: (
		unreadCommentIDSet: Set<string>,
		operation?: 'add' | 'remove' | 'removeAll' | 'initialize',
	) => void;
	/** Optional: reads the full comments query from the Apollo cache. */
	getCommentsDataFromCache?: typeof getCommentsDataFromCache;
	/** Optional: returns actions for the processed comments data store. */
	getProcessedCommentsDataActions?: () => { processCommentsData: ProcessCommentsData };
	/** Optional: triggers a refresh of the legacy comments panel store. */
	triggerCommentPanelRefresh?: () => void;
	/**
	 * Optional: tells the editor inline-comments annotation provider about
	 * reaction-created/deleted markers without mutating the editor document.
	 */
	syncEditorInlineReactionAnnotation?: (
		event: 'create' | 'delete',
		markerRef: string,
		step?: unknown,
	) => void;
	/** Optional: dispatch from `useSubscriptionsDispatchContext`. */
	addSubscriber?: SubscriptionsDispatchType['addSubscriber'];
	setContentId?: SubscriptionsDispatchType['setContentId'];
	setContentType?: SubscriptionsDispatchType['setContentType'];
};

export type InlineReactionsPubSubUnsubParams = {
	subscriptions: Array<{ subscriberId: string; delta: string }>;
	removeSubscriber?: SubscriptionsDispatchType['removeSubscriber'];
};

/** AGG subscription deltas for the three new inline-reaction events. */
export const InlineReactionSubscriptionDeltas = {
	InlineReactionAdded: 'inlineReactionAdded',
	InlineReactionRemoved: 'inlineReactionRemoved',
	InlineReactionHighlightDeleted: 'inlineReactionHighlightDeleted',
	InlineCommentUnresolved: 'inlineCommentUnresolved',
} as const;

export type InlineReactionDelta =
	(typeof InlineReactionSubscriptionDeltas)[keyof typeof InlineReactionSubscriptionDeltas];

/** Internal: returns true when the event was authored by the current user. */
const isSelfAuthored = (
	payload: ConfluenceOnContentModified | undefined,
	currentUserId: string | null,
) => Boolean(payload?.accountId && currentUserId && payload.accountId === currentUserId);

/**
 * Build a fresh `inlineReactionAdded` subscriber bound to the given params.
 *
 * We construct subscribers via factories (rather than module-scoped functions
 * sharing a mutable `params` slot) so each `setUpInlineReactionsPubSub`
 * invocation gets its own closure with no cross-mount state. This also means
 * tests don't need a module-level reset hook.
 */
export const createInlineReactionAddedHandler =
	(params: InlineReactionsPubSubParams) =>
	(_event: string, payload: ConfluenceOnContentModified): void => {
		if (isSelfAuthored(payload, params.currentUserId)) {
			return;
		}
		const event = payload?.inlineReactionAdded;
		if (!event || !event.commentId || !event.markerRef) {
			return;
		}

		const {
			addOptimisticReaction,
			applyRemoteDelta,
			markReactedUsersChanged,
			refetchReactionsSummary,
			refetchActiveInlineData,
			markDocumentNeedsUpdate,
			showReactionFlag,
			contentType,
			contentId,
			rendererActions,
			updateDocument,
			publishedDocumentVersionRef,
			addCommentInCache,
			fetchCommentById,
			updateUnreadCommentIDSet,
			getCommentsDataFromCache,
			getProcessedCommentsDataActions,
			triggerCommentPanelRefresh,
			syncEditorInlineReactionAnnotation,
			syncLegacyCommentsDataReactionsSummary,
			syncLegacyCommentsDataReactionThread,
			allowDocumentMutation = true,
			pageMode = PageMode.VIEW,
		} = params;
		const numericCommentId = extractNumericCommentId(event.commentId);

		if (event.newHighlight) {
			addOptimisticReaction(event.markerRef, event.commentId);
			refetchActiveInlineData();
			void syncLegacyCommentsDataReactionThread?.({
				commentId: numericCommentId,
				markerRef: event.markerRef,
			});

			if (
				allowDocumentMutation &&
				event.step &&
				event.publishVersionNumber != null &&
				event.publishVersionNumber === publishedDocumentVersionRef?.current
			) {
				// The event carries position data and the receiver's doc version matches —
				// apply the annotation mark directly for an instant highlight paint.
				try {
					const { from, to } = event.step;
					const result = rendererActions?.applyAnnotation(
						{ from, to },
						{
							annotationId: event.markerRef,
							annotationType: AnnotationTypes.INLINE_COMMENT,
						},
					);
					if (result && updateDocument) {
						updateDocument(result.doc as JSONDocNode);
					} else {
						markDocumentNeedsUpdate();
					}
				} catch {
					// Step position was invalid for the current doc —
					// deliver the highlight via a full page-content refetch instead.
					markDocumentNeedsUpdate();
				}
			} else {
				// Missing step, version mismatch, or publishVersionNumber absent —
				// deliver the highlight via a full page-content refetch instead.
				if (allowDocumentMutation) {
					markDocumentNeedsUpdate();
				}
			}

			if (!allowDocumentMutation) {
				syncEditorInlineReactionAnnotation?.('create', event.markerRef, event.step);
			}
		}

		// Apply the delta directly to the store for an instant count update.
		// Only when emojiId is present — without it we can't identify which
		// emoji to increment. The background refetch will always correct the
		// authoritative state regardless.
		if (event.emojiId) {
			applyRemoteDelta(numericCommentId, event.emojiId, 1);
			// Mark the facepile reactor list stale so the next mounted fetcher
			// refetches it — even if the popup/tile is currently closed.
			markReactedUsersChanged(numericCommentId, event.emojiId);
			syncLegacyCommentsDataReactionsSummary?.({
				actionType: 'add',
				commentId: numericCommentId,
				emojiId: event.emojiId,
				markerRef: event.markerRef,
			});
		}

		// Refetch for authoritative counts + `reacted` state for the current user.
		refetchReactionsSummary(event.commentId);

		// Update the comments panel cache so the new reaction appears without a
		// page refresh — feature parity with inline comments pubsub behaviour.
		// fetchCommentById fetches the updated comment (including its reactionsSummary)
		// and writes it into the shared cache that the comments panel reads from.
		void syncInlineReactionCommentToPanel({
			commentId: numericCommentId,
			contentId,
			pageMode,
			fetchCommentById,
			addCommentInCache,
			getCommentsDataFromCache,
			processCommentsData: getProcessedCommentsDataActions?.().processCommentsData,
			triggerCommentPanelRefresh,
		});

		// Mark the reaction comment as unread to trigger the blue-dot indicator
		// in the content wrapper — matching inline comment pubsub behaviour.
		if (updateUnreadCommentIDSet) {
			updateUnreadCommentIDSet(new Set([numericCommentId]), 'add');
		}

		showReactionFlag({
			commentId: event.commentId,
			markerRef: event.markerRef,
			emojiId: event.emojiId,
			contentType: contentType ?? 'page',
			actorAccountId: payload?.accountId,
		});
	};

/** See `createInlineReactionAddedHandler` for the factory rationale. */
export const createInlineReactionRemovedHandler =
	(params: InlineReactionsPubSubParams) =>
	(_event: string, payload: ConfluenceOnContentModified): void => {
		if (isSelfAuthored(payload, params.currentUserId)) {
			return;
		}
		const event = payload?.inlineReactionRemoved;
		if (!event || !event.commentId) {
			return;
		}
		const numericCommentId = extractNumericCommentId(event.commentId);

		// Apply the delta directly to the store for an instant count update.
		// Only when emojiId is present (same guard as the added handler).
		if (event.emojiId) {
			params.applyRemoteDelta(numericCommentId, event.emojiId, -1);
			// Mark the facepile reactor list stale (see added handler).
			params.markReactedUsersChanged(numericCommentId, event.emojiId);
			if (event.markerRef) {
				params.syncLegacyCommentsDataReactionsSummary?.({
					actionType: 'delete',
					commentId: numericCommentId,
					emojiId: event.emojiId,
					markerRef: event.markerRef,
				});
			}
		}

		// No flag for removals — matches inline-comment pubsub behaviour,
		// which only surfaces flags for new comments, not deletions.
		params.refetchReactionsSummary(event.commentId);

		void syncInlineReactionCommentToPanel({
			commentId: numericCommentId,
			contentId: params.contentId,
			pageMode: params.pageMode ?? PageMode.VIEW,
			fetchCommentById: params.fetchCommentById,
			addCommentInCache: params.addCommentInCache,
			getCommentsDataFromCache: params.getCommentsDataFromCache,
			processCommentsData: params.getProcessedCommentsDataActions?.().processCommentsData,
			triggerCommentPanelRefresh: params.triggerCommentPanelRefresh,
		});
	};

const getReopenedInlineCommentEvent = (event: string, payload: ConfluenceOnContentModified) =>
	event === InlineReactionSubscriptionDeltas.InlineCommentUnresolved
		? (payload.inlineCommentUnresolved ?? payload.inlineCommentRequestBody)
		: undefined;

const getFetchedCommentMarkerRef = (comment: unknown): string | undefined => {
	if (!comment || typeof comment !== 'object') {
		return undefined;
	}
	const location = (comment as { location?: { inlineMarkerRef?: string | null } | null }).location;
	return location?.inlineMarkerRef ?? undefined;
};

/** See `createInlineReactionAddedHandler` for the factory rationale. */
export const createInlineReactionReopenedHandler =
	(params: InlineReactionsPubSubParams) =>
	(event: string, payload: ConfluenceOnContentModified): void => {
		if (isSelfAuthored(payload, params.currentUserId)) {
			return;
		}

		const reopenedComment = getReopenedInlineCommentEvent(event, payload);
		if (!reopenedComment?.commentId || reopenedComment.inlineCommentType === 'REPLY') {
			return;
		}

		const markerRefFromPayload = reopenedComment.markerRef;
		if (
			markerRefFromPayload &&
			params.isOptimisticallyResolvedMarkerRef &&
			!params.isOptimisticallyResolvedMarkerRef(markerRefFromPayload)
		) {
			return;
		}

		const reopenedCommentId = reopenedComment.commentId;
		const numericCommentId = extractNumericCommentId(reopenedCommentId);
		const { fetchCommentById } = params;
		if (!fetchCommentById) {
			return;
		}

		void fetchCommentById({
			commentId: numericCommentId,
			contentId: params.contentId,
			fetchPolicy: 'network-only',
		})
			.then((fetchedComment) => {
				if (!fetchedComment || !isInlineReactionThread(fetchedComment)) {
					return;
				}

				const markerRef = markerRefFromPayload ?? getFetchedCommentMarkerRef(fetchedComment);
				if (!markerRef) {
					return;
				}
				if (
					params.isOptimisticallyResolvedMarkerRef &&
					!params.isOptimisticallyResolvedMarkerRef(markerRef)
				) {
					return;
				}

				params.removeOptimisticResolvedMarkerRef?.(markerRef);
				params.addOptimisticReaction(markerRef, reopenedCommentId);
				params.refetchActiveInlineData();
				params.addCommentInCache?.({ newComment: fetchedComment });
				if (params.getCommentsDataFromCache) {
					const updatedCommentsData = params.getCommentsDataFromCache(params.contentId);
					params.getProcessedCommentsDataActions?.().processCommentsData({
						data: updatedCommentsData,
						pageMode: params.pageMode ?? PageMode.VIEW,
					});
				}
				void params.syncLegacyCommentsDataReactionThread?.({
					commentId: numericCommentId,
					markerRef,
				});
				if (params.allowDocumentMutation === false) {
					params.syncEditorInlineReactionAnnotation?.('create', markerRef);
					void params.triggerCommentPanelRefresh?.();
				}
			})
			.catch((error: unknown) => {
				logger.error`Failed to fetch reopened inline reaction comment ${error}`;
			});
	};

/** See `createInlineReactionAddedHandler` for the factory rationale. */
export const createInlineReactionHighlightDeletedHandler =
	(params: InlineReactionsPubSubParams) =>
	(_event: string, payload: ConfluenceOnContentModified): void => {
		if (isSelfAuthored(payload, params.currentUserId)) {
			return;
		}
		const event = payload?.inlineReactionHighlightDeleted;
		if (!event || !event.markerRef) {
			return;
		}

		const {
			rendererActions,
			updateDocument,
			pruneOptimisticReactions,
			setActiveHighlight,
			refetchActiveInlineData,
			allowDocumentMutation = true,
			deleteCommentInCache,
			updateUnreadCommentIDSet,
			getCommentsDataFromCache,
			getProcessedCommentsDataActions,
			triggerCommentPanelRefresh,
			contentId,
			pageMode = PageMode.VIEW,
			syncEditorInlineReactionAnnotation,
		} = params;
		const numericCommentId = event.commentId ? extractNumericCommentId(event.commentId) : undefined;

		if (allowDocumentMutation) {
			const deleteResult = rendererActions?.deleteAnnotation(
				event.markerRef,
				AnnotationTypes.INLINE_COMMENT,
			);
			if (deleteResult && updateDocument) {
				updateDocument(deleteResult.doc as JSONDocNode);
			}
		} else {
			syncEditorInlineReactionAnnotation?.('delete', event.markerRef);
		}

		// Drop the optimistic entry (if any) so the renderer doesn't try to
		// keep painting the highlight after the mark is removed.
		pruneOptimisticReactions([event.markerRef]);
		setActiveHighlight?.(null);

		// Refresh the active-inline-data cache so the markerRef set is
		// correct on subsequent renders.
		refetchActiveInlineData();

		if (!allowDocumentMutation && numericCommentId) {
			deleteCommentInCache?.({ commentId: numericCommentId });
			updateUnreadCommentIDSet?.(new Set([numericCommentId]), 'remove');
			if (getCommentsDataFromCache) {
				const updatedCommentsData = getCommentsDataFromCache(contentId);
				getProcessedCommentsDataActions?.().processCommentsData({
					data: updatedCommentsData,
					pageMode,
				});
			}
		}
		if (!allowDocumentMutation) {
			void triggerCommentPanelRefresh?.();
		}
	};

export const setUpInlineReactionsPubSub = (
	params: InlineReactionsPubSubParams,
): Array<{ subscriberId: string; delta: string }> => {
	const { addSubscriber, setContentId, setContentType, contentId, contentType } = params;
	if (!addSubscriber || !setContentId || !setContentType) {
		return [];
	}

	setContentType(contentType ?? 'page');
	setContentId(contentId);

	const subscribers = [
		{
			subscriberId: 'inlineReactionsPubSubInlineReactionAdded',
			onSubscriptionData: createInlineReactionAddedHandler(params),
			delta: InlineReactionSubscriptionDeltas.InlineReactionAdded,
		},
		{
			subscriberId: 'inlineReactionsPubSubInlineReactionRemoved',
			onSubscriptionData: createInlineReactionRemovedHandler(params),
			delta: InlineReactionSubscriptionDeltas.InlineReactionRemoved,
		},
		{
			subscriberId: 'inlineReactionsPubSubInlineReactionHighlightDeleted',
			onSubscriptionData: createInlineReactionHighlightDeletedHandler(params),
			delta: InlineReactionSubscriptionDeltas.InlineReactionHighlightDeleted,
		},
		{
			subscriberId: 'inlineReactionsPubSubInlineCommentUnresolved',
			onSubscriptionData: createInlineReactionReopenedHandler(params),
			delta: InlineReactionSubscriptionDeltas.InlineCommentUnresolved,
		},
	];

	return subscribers.map((subscriber) => ({
		subscriberId: addSubscriber(subscriber),
		delta: subscriber.delta,
	}));
};

export const removeInlineReactionsPubSub = ({
	subscriptions,
	removeSubscriber,
}: InlineReactionsPubSubUnsubParams): void => {
	if (!removeSubscriber) {
		return;
	}
	for (const subscription of subscriptions) {
		removeSubscriber(subscription);
	}
};
